import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LayoutGrid, List, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchCampaigns, updateCampaign, createCampaign } from "@/lib/api";
import { CAMPAIGN_STAGES, formatINR } from "@/lib/format";
import { KanbanBoard, KanbanCard } from "@/components/Kanban";
import FormDialog from "@/components/FormDialog";

const stageColor = (s) => {
  const map = {
    Lead: "bg-zinc-500/10 text-zinc-300 border-zinc-700",
    "Proposal Sent": "bg-amber-500/10 text-amber-300 border-amber-500/30",
    Negotiation: "bg-purple-500/10 text-purple-300 border-purple-500/30",
    Active: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    "Deliverables Pending": "bg-orange-500/10 text-orange-300 border-orange-500/30",
    "In Review": "bg-blue-500/10 text-blue-300 border-blue-500/30",
    Completed: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
    Paid: "bg-teal-500/10 text-teal-300 border-teal-500/30",
    Archived: "bg-zinc-500/10 text-zinc-500 border-zinc-800",
  };
  return map[s] || "bg-zinc-500/10 text-zinc-400 border-zinc-700";
};

const Campaigns = () => {
  const [view, setView] = useState("list");
  const [campaigns, setCampaigns] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCampaigns().then(setCampaigns).catch(() => setCampaigns([]));
  }, []);

  const grouped = useMemo(() => {
    const out = {};
    CAMPAIGN_STAGES.forEach((s) => (out[s] = []));
    (campaigns || []).forEach((c) => out[c.stage]?.push(c));
    return out;
  }, [campaigns]);

  const onMove = async (id, newStage) => {
    const prev = campaigns;
    setCampaigns((cs) => cs.map((c) => (c.id === id ? { ...c, stage: newStage } : c)));
    try {
      await updateCampaign(id, { stage: newStage });
      toast.success(`Moved to ${newStage}`);
    } catch (_e) {
      setCampaigns(prev);
      toast.error("Could not move");
    }
  };

  if (campaigns === null) {
    return (
      <div className="flex items-center justify-center py-20 text-zinc-500">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Campaigns</h1>
          <p className="text-sm text-zinc-500 mt-1">{campaigns.length} campaigns across pipeline</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-0.5 flex">
            <button onClick={() => setView("list")} className={`h-8 px-3 rounded-md text-xs inline-flex items-center gap-1.5 ${view === "list" ? "bg-white/[0.06] text-zinc-100" : "text-zinc-400"}`} data-testid="campaigns-view-list">
              <List className="h-3.5 w-3.5" /> List
            </button>
            <button onClick={() => setView("kanban")} className={`h-8 px-3 rounded-md text-xs inline-flex items-center gap-1.5 ${view === "kanban" ? "bg-white/[0.06] text-zinc-100" : "text-zinc-400"}`} data-testid="campaigns-view-kanban">
              <LayoutGrid className="h-3.5 w-3.5" /> Kanban
            </button>
          </div>
          <FormDialog
            testid="campaign-create"
            title="New campaign"
            description="Spin up a new campaign. You can add deliverables and assign creators inside the campaign detail page."
            submitLabel="Create Campaign"
            trigger={
              <Button className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="add-campaign-btn">
                + New Campaign
              </Button>
            }
            fields={[
              { key: "name", label: "Campaign name", required: true, placeholder: "Nykaa Festive Glow", gridCols: 2 },
              { key: "brand", label: "Brand", required: true, placeholder: "Nykaa", gridCols: 2 },
              {
                key: "stage",
                label: "Stage",
                type: "select",
                options: CAMPAIGN_STAGES,
                gridCols: 2,
              },
              { key: "platform", label: "Platform(s)", placeholder: "Instagram, YouTube", gridCols: 2 },
              { key: "budget", label: "Budget (₹)", type: "number", placeholder: "1400000", gridCols: 2 },
              { key: "creatorCount", label: "Creators", type: "number", placeholder: "8", gridCols: 2 },
              { key: "startDate", label: "Start date", type: "date", gridCols: 2 },
              { key: "endDate", label: "End date", type: "date", gridCols: 2 },
              { key: "objective", label: "Objective", type: "textarea", placeholder: "Drive trials for…" },
            ]}
            onSubmit={async (form) => {
              const body = {
                name: form.name,
                brand: form.brand,
                stage: form.stage || "Lead",
                platform: form.platform || "Instagram",
                budget: Number(form.budget) || 0,
                creatorCount: Number(form.creatorCount) || 0,
                startDate: form.startDate || "",
                endDate: form.endDate || "",
                objective: form.objective || "",
                creators: [],
                deliverables: [],
              };
              const created = await createCampaign(body);
              setCampaigns((cs) => [...cs, created]);
              toast.success(`Campaign '${created.name}' created`);
            }}
          />
        </div>
      </div>

      {view === "list" ? (
        <div className="rounded-xl border border-white/8 bg-white/[0.02] overflow-x-auto">
          <table className="w-full text-sm" data-testid="campaigns-table">
            <thead className="text-[11px] uppercase tracking-wider text-zinc-500 bg-white/[0.02]">
              <tr className="text-left">
                <th className="py-3 px-4 font-medium">Campaign</th>
                <th className="py-3 px-4 font-medium">Brand</th>
                <th className="py-3 px-4 font-medium">Stage</th>
                <th className="py-3 px-4 font-medium">Creators</th>
                <th className="py-3 px-4 font-medium">Start</th>
                <th className="py-3 px-4 font-medium">Budget</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {campaigns.map((c) => (
                <tr
                  key={c.id}
                  className="hover:bg-white/[0.02] cursor-pointer"
                  data-testid={`campaign-row-${c.id}`}
                  onClick={() => navigate(`/app/campaigns/${c.id}`)}
                >
                  <td className="py-3 px-4">
                    <Link to={`/app/campaigns/${c.id}`} className="text-zinc-100 hover:text-blue-300 font-medium" onClick={(e) => e.stopPropagation()}>{c.name}</Link>
                  </td>
                  <td className="py-3 px-4 text-zinc-300">{c.brand}</td>
                  <td className="py-3 px-4">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${stageColor(c.stage)}`}>{c.stage}</span>
                  </td>
                  <td className="py-3 px-4 text-zinc-300">{c.creatorCount}</td>
                  <td className="py-3 px-4 text-zinc-400">{c.startDate}</td>
                  <td className="py-3 px-4 text-zinc-300">{formatINR(c.budget)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div data-testid="campaigns-kanban">
          <KanbanBoard
            columns={CAMPAIGN_STAGES.map((s) => ({ id: s, title: s }))}
            itemsByColumn={grouped}
            onMove={onMove}
            renderCard={(c) => (
              <KanbanCard
                key={c.id}
                id={c.id}
                onClick={() => navigate(`/app/campaigns/${c.id}`)}
              >
                <div className="text-sm font-medium text-zinc-100">{c.name}</div>
                <div className="mt-1 text-xs text-zinc-500">{c.brand}</div>
                <div className="mt-3 flex items-center justify-between text-[11px]">
                  <span className="text-zinc-400">{c.creatorCount} creators</span>
                  <span className="text-blue-300 font-medium">{formatINR(c.budget)}</span>
                </div>
              </KanbanCard>
            )}
          />
          <p className="text-[11px] text-zinc-500 mt-3 px-1">Tip: drag a card to another column to move it.</p>
        </div>
      )}
    </div>
  );
};

export default Campaigns;
